import {forEach, isEmptyString} from './common';
import {isSafeHref} from './safeHref';
import {
	FILE_CHIP_CLASS,
	FILE_CHIP_FALLBACK_LABEL,
	FILE_REF_ATTRIBUTE,
	isFileReference,
} from '../../../task-modal/inlineImages/fileReference';

/**
 * Mapping block-type to corresponding html tag.
 */
const blockTypesMapping = {
	unstyled: 'p',
	'header-one': 'h1',
	'header-two': 'h2',
	'header-three': 'h3',
	'header-four': 'h4',
	'header-five': 'h5',
	'header-six': 'h6',
	'unordered-list-item': 'ul',
	'ordered-list-item': 'ol',
	blockquote: 'blockquote',
	code: 'pre',
	jiraTable: 'p',
	jiraPanelStart: 'p',
	jiraPanelEnd: 'p',
	jiraSingleMedia: 'p',
	jiraMediaGroup: 'p',
	divider: 'hr',
};

const isSelfClosingTag = type => 'hr'.includes(type);

/**
 * Array of types which should be parsed as jira noneditable blocks
 */
const jiraNoneditableTypes = ['jiraTable', 'jiraPanelStart', 'jiraPanelEnd', 'jiraSingleMedia', 'jiraMediaGroup'];

const blockClassTypesMapping = {};

/**
 * Function will return HTML tag for a block.
 */
export function getBlockTag(type) {
	return type && blockTypesMapping[type];
}

export function getBlockClass(type) {
	return type && blockClassTypesMapping[type];
}

/**
 * Function will return style string for a block.
 */
export function getBlockStyle(data) {
	let styles = '';
	forEach(data, (key, value) => {
		if (value) {
			styles += `${key}:${value};`;
		}
	});
	return styles;
}

export function isJiraNoneditable(type) {
	return type && jiraNoneditableTypes.includes(type);
}

/**
 * The function returns an array of hashtag-sections in blocks.
 * These will be areas in block which have hashtags applicable to them.
 */
function getHashtagRanges(blockText, hashtagConfig) {
	const sections = [];
	if (hashtagConfig) {
		let counter = 0;
		let startIndex = 0;
		let text = blockText;
		const trigger = hashtagConfig.trigger || '#';
		const separator = hashtagConfig.separator || ' ';
		for (; text.length > 0 && startIndex >= 0; ) {
			if (text[0] === trigger) {
				startIndex = 0;
				counter = 0;
				text = text.substr(trigger.length);
			} else {
				startIndex = text.indexOf(separator + trigger);
				if (startIndex >= 0) {
					text = text.substr(startIndex + (separator + trigger).length);
					counter += startIndex + separator.length;
				}
			}
			if (startIndex >= 0) {
				const endIndex = text.indexOf(separator) >= 0 ? text.indexOf(separator) : text.length;
				const hashtag = text.substr(0, endIndex);
				if (hashtag && hashtag.length > 0) {
					sections.push({
						offset: counter,
						length: hashtag.length + trigger.length,
						type: 'HASHTAG',
					});
				}
				counter += trigger.length;
			}
		}
	}
	return sections;
}

/**
 * The function returns an array of entity-sections in blocks.
 * These will be areas in block which have same entity or no entity applicable to them.
 */
function getSections(block, hashtagConfig) {
	const sections = [];
	let lastOffset = 0;
	let sectionRanges = block.entityRanges.map(range => {
		const {offset, length, key} = range;
		return {
			offset,
			length,
			key,
			type: 'ENTITY',
		};
	});
	sectionRanges = sectionRanges.concat(getHashtagRanges(block.text, hashtagConfig));
	sectionRanges = sectionRanges.sort((s1, s2) => s1.offset - s2.offset);
	sectionRanges.forEach(r => {
		if (r.offset > lastOffset) {
			sections.push({
				start: lastOffset,
				end: r.offset,
			});
		}
		sections.push({
			start: r.offset,
			end: r.offset + r.length,
			entityKey: r.key,
			type: r.type,
		});
		lastOffset = r.offset + r.length;
	});
	if (lastOffset < block.text.length) {
		sections.push({
			start: lastOffset,
			end: block.text.length,
		});
	}
	return sections;
}

/**
 * Function to check if the block is an atomic entity block.
 */
function isAtomicEntityBlock(block) {
	if (block.entityRanges.length > 0 && (isEmptyString(block.text) || block.type === 'atomic')) {
		return true;
	}
	return false;
}

/**
 * The function will return array of inline styles applicable to the block.
 */
function getStyleArrayForBlock(block) {
	const {text, inlineStyleRanges} = block;
	const inlineStyles = {
		BOLD: new Array(text.length),
		ITALIC: new Array(text.length),
		UNDERLINE: new Array(text.length),
		STRIKETHROUGH: new Array(text.length),
		CODE: new Array(text.length),
		SUPERSCRIPT: new Array(text.length),
		SUBSCRIPT: new Array(text.length),
		COLOR: new Array(text.length),
		BGCOLOR: new Array(text.length),
		FONTSIZE: new Array(text.length),
		FONTFAMILY: new Array(text.length),
		length: text.length,
	};
	if (inlineStyleRanges && inlineStyleRanges.length > 0) {
		inlineStyleRanges.forEach(range => {
			const {offset} = range;
			const length = offset + range.length;
			for (let i = offset; i < length; i += 1) {
				if (range.style.indexOf('color-') === 0) {
					inlineStyles.COLOR[i] = range.style.substring(6);
				} else if (range.style.indexOf('bgcolor-') === 0) {
					inlineStyles.BGCOLOR[i] = range.style.substring(8);
				} else if (range.style.indexOf('fontsize-') === 0) {
					inlineStyles.FONTSIZE[i] = range.style.substring(9);
				} else if (range.style.indexOf('fontfamily-') === 0) {
					inlineStyles.FONTFAMILY[i] = range.style.substring(11);
				} else if (inlineStyles[range.style]) {
					inlineStyles[range.style][i] = true;
				}
			}
		});
	}
	return inlineStyles;
}

/**
 * The function will return inline style applicable at some offset within a block.
 */
export function getStylesAtOffset(inlineStyles, offset) {
	const styles = {};
	if (inlineStyles.COLOR[offset]) {
		styles.COLOR = inlineStyles.COLOR[offset];
	}
	if (inlineStyles.BGCOLOR[offset]) {
		styles.BGCOLOR = inlineStyles.BGCOLOR[offset];
	}
	if (inlineStyles.FONTSIZE[offset]) {
		styles.FONTSIZE = inlineStyles.FONTSIZE[offset];
	}
	if (inlineStyles.FONTFAMILY[offset]) {
		styles.FONTFAMILY = inlineStyles.FONTFAMILY[offset];
	}
	if (inlineStyles.UNDERLINE[offset]) {
		styles.UNDERLINE = true;
	}
	if (inlineStyles.ITALIC[offset]) {
		styles.ITALIC = true;
	}
	if (inlineStyles.BOLD[offset]) {
		styles.BOLD = true;
	}
	if (inlineStyles.STRIKETHROUGH[offset]) {
		styles.STRIKETHROUGH = true;
	}
	if (inlineStyles.CODE[offset]) {
		styles.CODE = true;
	}
	if (inlineStyles.SUBSCRIPT[offset]) {
		styles.SUBSCRIPT = true;
	}
	if (inlineStyles.SUPERSCRIPT[offset]) {
		styles.SUPERSCRIPT = true;
	}
	return styles;
}

/**
 * Function returns true for a set of styles if the value of these styles at an offset
 * are same as that on the previous offset.
 */
export function sameStyleAsPrevious(inlineStyles, styles, index) {
	let sameStyled = true;
	if (index > 0 && index < inlineStyles.length) {
		styles.forEach(style => {
			sameStyled = sameStyled && inlineStyles[style][index] === inlineStyles[style][index - 1];
		});
	} else {
		sameStyled = false;
	}
	return sameStyled;
}

/**
 * Function returns html for text depending on inline style tags applicable to it.
 */
export function addInlineStyleMarkup(style, content) {
	if (style === 'BOLD') {
		return `<strong>${content}</strong>`;
	}
	if (style === 'ITALIC') {
		return `<em>${content}</em>`;
	}
	if (style === 'UNDERLINE') {
		return `<u>${content}</u>`;
	}
	if (style === 'STRIKETHROUGH') {
		return `<s>${content}</s>`;
	}
	if (style === 'CODE') {
		return `<code>${content}</code>`;
	}
	if (style === 'SUPERSCRIPT') {
		return `<sup>${content}</sup>`;
	}
	if (style === 'SUBSCRIPT') {
		return `<sub>${content}</sub>`;
	}
	return content;
}

/**
 * The function returns text for given section of block after doing required character replacements.
 */
function getSectionText(text) {
	if (text && text.length > 0) {
		const chars = text.map(ch => {
			switch (ch) {
				case '\n':
					return '<br>';
				case '&':
					return '&amp;';
				case '<':
					return '&lt;';
				case '>':
					return '&gt;';
				default:
					return ch;
			}
		});
		return chars.join('');
	}
	return '';
}

/**
 * Function returns html for text depending on inline style tags applicable to it.
 */
export function addStylePropertyMarkup(styles, text) {
	if (styles && (styles.COLOR || styles.BGCOLOR || styles.FONTSIZE || styles.FONTFAMILY)) {
		let styleString = 'style="';
		if (styles.COLOR) {
			styleString += `color: ${styles.COLOR};`;
		}
		if (styles.BGCOLOR) {
			styleString += `background-color: ${styles.BGCOLOR};`;
		}
		if (styles.FONTSIZE) {
			styleString += `font-size: ${styles.FONTSIZE}${/^\d+$/.test(styles.FONTSIZE) ? 'px' : ''};`;
		}
		if (styles.FONTFAMILY) {
			styleString += `font-family: ${styles.FONTFAMILY};`;
		}
		styleString += '"';
		return `<span ${styleString}>${text}</span>`;
	}
	return text;
}

// Escape a value for safe use inside a double-quoted HTML attribute, so a stray quote/angle
// bracket in an href cannot break out of the attribute or the tag (PIKA-542 #4).
function escapeHtmlAttribute(value) {
	return String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// A CSS length is validated rather than escaped, because a style attribute is a second parser:
// escaping leaves ';', '(' and ')' intact, so an escaped value can still close one declaration and
// open another (a url() background exfiltrates on render). Anything unrecognised returns null and
// is dropped by the caller — never coerced to a default, so a value that never had a dimension
// does not acquire one on the next save.
const CSS_LENGTH = /^-?\d+(\.\d+)?(px|%|r?em|vh|vw)$/;

function cssLength(value) {
	return typeof value === 'string' && CSS_LENGTH.test(value.trim()) ? value.trim() : null;
}

// text-align is an allowlist for the same reason. The values reaching here come from a float on
// parse, so left/right/none is the realistic set; center and justify are included as the other
// values the property legitimately takes.
const SAFE_TEXT_ALIGN = ['left', 'right', 'center', 'justify'];

function cssTextAlign(value) {
	return typeof value === 'string' && SAFE_TEXT_ALIGN.includes(value.trim()) ? value.trim() : null;
}

/**
 * Function will return markup for Entity.
 */
/**
 * The `data-name` / `data-size` / `data-mime` triplet every inline-media element carries.
 *
 * Shared by the player and the chip because they must agree: a player degrades to a chip
 * on a decode failure or with no adapter, and the chip then displays whatever the last
 * load restored. Emitting a different set for each kind meant a player lost its size and
 * type on the first save, so the fallback got worse every lap.
 *
 * `data-size` is tested as a non-negative safe integer and OMITTED rather than emptied
 * when it is not one. `Number('')` and `Number(null)` are both 0 — finite and >= 0 — so
 * writing `data-size=""` for an unknown size makes a chip state on screen that a real
 * file is 0 B, and it re-emits as `data-size="0"`, which parses back as a legitimate
 * zero. After one round trip nothing can distinguish that from a genuinely empty file. A
 * chip restored from a Jira marker carries the name only, so this is a live path. A
 * genuine 0 still emits: "empty" and "unknown" are different facts and only one of them
 * is unrecoverable. `isSafeInteger` rather than `isFinite`, because a byte count of 12.5
 * or 1e21 is not a byte count and re-emitting one launders it into a value the parse
 * side accepts.
 */
function mediaMetadataAttributes(data) {
	const name = escapeHtmlAttribute(data.name ?? '');
	const size = Number.isSafeInteger(data.size) && data.size >= 0 ? ` data-size="${data.size}"` : '';
	const mime = escapeHtmlAttribute(data.mimeType ?? '');
	return `data-name="${name}"${size} data-mime="${mime}"`;
}

function getEntityMarkup(entityMap, entityKey, text, customEntityTransform) {
	const entity = entityMap[entityKey];
	if (typeof customEntityTransform === 'function') {
		const html = customEntityTransform(entity, text);
		if (html) {
			return html;
		}
	}
	// Normalised once. Stored content spells the type in upper case and Plate writes it
	// lower case, so every branch below compares case-insensitively; doing it here keeps
	// the chain to one comparison value instead of one per rung, and a non-string type in
	// malformed stored content yields undefined rather than throwing.
	const entityType = typeof entity?.type === 'string' ? entity.type.toUpperCase() : undefined;
	if (entityType === 'MENTION') {
		if (entity?.data?.mention?.jiraData) {
			const dataObject = entity?.data?.mention;
			const dataSlateValue = escapeDataString(dataObject);
			return `<span class="slate-mention" data-slate-value="${dataSlateValue}">
					${text}
				</span>`;
		} else {
			return `<span class="slate-mention" data-slate-value="${entity.data.mention.personId}">${text}</span>`;
		}
	}
	if (entityType === 'LINK') {
		// Degrade to plain text rather than emit an empty/unsafe-scheme anchor (PIKA-542 #3, #4).
		if (!isSafeHref(entity.data.url)) {
			return text;
		}
		const targetOption = entity.data.targetOption || '_self';
		// `targetOption` comes from an unrestricted `node.target` on parse, so escape it exactly like
		// href to close the same attribute break-out on the same tag (PIKA-542 #4).
		return `<a href="${escapeHtmlAttribute(entity.data.url)}" target="${escapeHtmlAttribute(targetOption)}">${text}</a>`;
	}
	if (entityType === 'JIRA_INLINECARD') {
		// Jira smart-link (inline card). Emit a class-tagged anchor so getEntityId can round-trip
		// it back to a JIRA_INLINECARD entity instead of degrading it to plain text (PIKA-542).
		// A missing/unsafe href degrades to plain text instead of emitting a broken anchor (#3, #4).
		//
		// Accept `url` as well as `href`: a card authored in Jira arrives carrying the raw ADF
		// inlineCard attrs, where the key is `url`, so it has no href at all and would otherwise
		// degrade to plain text. `href` is preferred because that is what this serializer writes,
		// so a card edited in Forecast keeps the value the user last saw.
		const cardHref = entity.data.href ?? entity.data.url;
		if (!isSafeHref(cardHref)) {
			return text;
		}
		return `<a href="${escapeHtmlAttribute(cardHref)}" class="slate-inline-card">${text}</a>`;
	}
	if (entityType === 'IMAGE') {
		// This html is handed back to the editor as initialHtml and parsed as DOM, so every
		// interpolated value is a potential attribute break-out. `alt` carries the uploaded
		// filename and `src` a caller-supplied reference, so both are user-controlled — the
		// sibling LINK and JIRA_INLINECARD branches escape for the same reason.
		//
		// A missing src would otherwise emit src="undefined" or src="", either of which makes
		// the browser issue a spurious request; emit nothing instead.
		if (!entity.data.src) {
			return '';
		}
		// `src` is escaped but deliberately NOT passed through isSafeHref: that allowlist is
		// http/https/mailto, so the internal file-reference scheme would fail it and every
		// inline image would degrade. Escaping is what closes the actual vector here, and an
		// <img src> does not execute javascript: URLs.
		const src = escapeHtmlAttribute(entity.data.src);
		const alt = escapeHtmlAttribute(entity.data.alt ?? '');
		// The dimensions and alignment land inside a style attribute, where escaping alone is
		// not enough — it leaves ';', '(' and ')' intact, so an escaped value can still inject
		// further declarations (a url() background exfiltrates on render). Validate instead,
		// and drop anything unrecognised rather than coercing it, so a value that never had a
		// dimension does not acquire one on the next save.
		const declarations = [
			['height', cssLength(entity.data.height)],
			['width', cssLength(entity.data.width)],
		].filter(([, value]) => value !== null);
		const style = declarations.length ? ` style="${declarations.map(([k, v]) => `${k}: ${v}`).join(';')}"` : '';
		const img = `<img src="${src}" alt="${alt}"${style}/>`;

		const textAlign = cssTextAlign(entity.data.alignment);
		if (textAlign) {
			return `<div style="text-align:${textAlign};">${img}</div>`;
		}
		return img;
	}
	// An inline video or audio reference. Both are atomic blocks, so `text` is empty
	// here and the reference is the entire content of the block.
	if (entityType === 'VIDEO' || entityType === 'AUDIO') {
		// Only our own references are emitted, and a missing one emits nothing.
		//
		// A src that is not a `forecast-file:` reference is a third-party URL, and this
		// html is handed straight back to the editor as initialHtml, so emitting one makes
		// every viewer's browser fetch media from someone else's host on every open. Such
		// entities exist in stored content already — a paste of web-page html produced them
		// back when nothing emitted <video> — and the inbound guard in htmlToDraftjs can
		// only drop them after a save, which is too late to stop the request. A missing src
		// would emit src="undefined" or src="", either of which makes the browser issue a
		// spurious request; both cases emit nothing, as the IMAGE branch does for a
		// missing src.
		if (!isFileReference(entity.data?.src)) {
			return '';
		}
		const tag = entityType.toLowerCase();
		// This html is handed back to the editor as initialHtml and parsed as DOM, so
		// every interpolated value is a potential attribute break-out; the name carries a
		// user-supplied filename. Deliberately NOT passed through isSafeHref: that
		// allowlist is http/https/mailto, so the internal file-reference scheme would fail
		// it and every inline player would degrade to nothing. Escaping is what closes the
		// actual vector, and a media element's src does not execute javascript: URLs.
		const src = escapeHtmlAttribute(entity.data.src);
		// No geometry and no alignment, unlike the IMAGE branch above. A player element
		// sizes itself in CSS and ignores height, width and float, and the parse side reads
		// none of the three, so a style attribute here would be discarded on the next lap
		// while looking supported — and the `<div style="text-align:…">` wrapper the IMAGE
		// branch uses deserializes into a stray empty paragraph above the player, since
		// `div` aliases the unstyled block type. If a player ever needs geometry, the
		// element and the parse side have to come first.
		//
		// Size and mime ARE carried, on the same terms as the chip. A player falls back to
		// a chip whenever the browser cannot decode the file or no adapter resolves it, and
		// the chip can only show what the last load restored — so dropping them here made
		// the fallback lose its size and type on every save/reload, degrading a little
		// further each lap. The design system emits the same three attributes on a player
		// for exactly this reason.
		return `<${tag} src="${src}" ${mediaMetadataAttributes(entity.data)}></${tag}>`;
	}
	// A file chip. Unlike the other media kinds this is an INLINE entity inside a
	// paragraph rather than an atomic block, which is the whole point of it — it sits at
	// the position in the sentence that refers to the file. So `text` is the visible
	// label, and it is written back as the span's content while `data-name` carries the
	// stored name.
	//
	// The two cannot disagree for long: the chip is parsed back as an IMMUTABLE entity,
	// so the label cannot be retyped in the legacy editor, and the design system reads
	// the name off `data-name` and ignores the span's children. `text` is preferred here
	// only so a label already stored by an older, MUTABLE chip survives the lap it takes
	// to be re-parsed as immutable.
	//
	// No href and no src, deliberately. isSafeHref allowlists http/https/mailto, so a
	// forecast-file: href would fail it and the chip would degrade to plain text. The
	// IMAGE branch's bypass of that guard does not transfer — it is safe there only
	// because an <img src> does not execute javascript: URLs, and an <a href> does. An
	// inert span never raises the question, and the click is a handler, not navigation.
	if (entityType === 'FILE') {
		// Degrade to the visible text, exactly as the LINK and JIRA_INLINECARD branches do,
		// and never to ''. This is an inline entity, so the caller substitutes whatever this
		// returns for the whole range: returning '' would delete the words the chip sits
		// among, turning "see report.pdf for detail" into "see  for detail".
		//
		// Only our own references are accepted. A third-party one is dropped by the parse on
		// the way back in, so emitting a chip for it would lose the label as well as the
		// reference.
		if (!isFileReference(entity.data?.src)) {
			return text ?? '';
		}
		const ref = escapeHtmlAttribute(entity.data.src);
		// `text` is interpolated RAW, exactly as the LINK, JIRA_INLINECARD and ACTION_ITEM
		// branches above do, and deliberately NOT escaped again. By the time it reaches this
		// function it has already been through getSectionText, which maps & < > to entities,
		// and it may already carry the inline-style markup
		// getInlineStyleSectionMarkup wrapped around it. Escaping a second time renders
		// `Q1 & Q2.pdf` as a visible `Q1 &amp; Q2.pdf` that grows another `amp;` on every
		// save — unboundedly, because the editor reloads and re-serializes this HTML — and
		// turns a styled label's own <span> tags into literal visible text.
		//
		// Only the data.name fallback is escaped, because that value has never been through
		// getSectionText.
		//
		// Never empty. A chip with no label has no character for the parse side to attach
		// the entity to, so an empty one comes back with its reference gone — the parse
		// side anchors to this same constant for the shapes it cannot control.
		const label = text || escapeHtmlAttribute(entity.data.name || FILE_CHIP_FALLBACK_LABEL);
		return (
			`<span class="${FILE_CHIP_CLASS}" ${FILE_REF_ATTRIBUTE}="${ref}" ` +
			`${mediaMetadataAttributes(entity.data)}>${label}</span>`
		);
	}
	if (entityType === 'EMBEDDED_LINK') {
		return `<iframe width="${entity.data.width}" height="${entity.data.height}" src="${entity.data.src}" frameBorder="0"></iframe>`;
	}
	if (entityType === 'ACTION_ITEM') {
		return `<div class="slate-action_item"><button role="checkbox" data-state="${
			entity.data.checked ? 'checked' : 'unchecked'
		}" />${text}</div>`;
	}
	return text;
}

/**
 * For a given section in a block the function will return a further list of sections,
 * with similar inline styles applicable to them.
 */
function getInlineStyleSections(block, styles, start, end) {
	const styleSections = [];
	const text = Array.from(block.text);
	if (text.length > 0) {
		const inlineStyles = getStyleArrayForBlock(block);
		let section;
		for (let i = start; i < end; i += 1) {
			if (i !== start && sameStyleAsPrevious(inlineStyles, styles, i)) {
				section.text.push(text[i]);
				section.end = i + 1;
			} else {
				section = {
					styles: getStylesAtOffset(inlineStyles, i),
					text: [text[i]],
					start: i,
					end: i + 1,
				};
				styleSections.push(section);
			}
		}
	}
	return styleSections;
}

/**
 * Replace leading blank spaces by &nbsp;
 */
export function trimLeadingZeros(sectionText) {
	if (sectionText) {
		let replacedText = sectionText;
		for (let i = 0; i < replacedText.length; i += 1) {
			if (sectionText[i] === ' ') {
				replacedText = replacedText.replace(' ', '&nbsp;');
			} else {
				break;
			}
		}
		return replacedText;
	}
	return sectionText;
}

/**
 * Replace trailing blank spaces by &nbsp;
 */
export function trimTrailingZeros(sectionText) {
	if (sectionText) {
		let replacedText = sectionText;
		for (let i = replacedText.length - 1; i >= 0; i -= 1) {
			if (replacedText[i] === ' ') {
				replacedText = `${replacedText.substring(0, i)}&nbsp;${replacedText.substring(i + 1)}`;
			} else {
				break;
			}
		}
		return replacedText;
	}
	return sectionText;
}

/**
 * The method returns markup for section to which inline styles
 * like BOLD, ITALIC, UNDERLINE, STRIKETHROUGH, CODE, SUPERSCRIPT, SUBSCRIPT are applicable.
 */
function getStyleTagSectionMarkup(styleSection) {
	const {styles, text} = styleSection;
	let content = getSectionText(text);
	forEach(styles, (style, value) => {
		content = addInlineStyleMarkup(style, content, value);
	});
	return content;
}

/**
* The method returns markup for section to which inline styles
like color, background-color, font-size are applicable.
*/
function getInlineStyleSectionMarkup(block, styleSection) {
	const styleTagSections = getInlineStyleSections(
		block,
		['BOLD', 'ITALIC', 'UNDERLINE', 'STRIKETHROUGH', 'CODE', 'SUPERSCRIPT', 'SUBSCRIPT'],
		styleSection.start,
		styleSection.end
	);
	let styleSectionText = '';
	styleTagSections.forEach(stylePropertySection => {
		styleSectionText += getStyleTagSectionMarkup(stylePropertySection);
	});
	styleSectionText = addStylePropertyMarkup(styleSection.styles, styleSectionText);
	return styleSectionText;
}

/*
 * The method returns markup for an entity section.
 * An entity section is a continuous section in a block
 * to which same entity or no entity is applicable.
 */
function getSectionMarkup(block, entityMap, section, customEntityTransform) {
	const entityInlineMarkup = [];
	const inlineStyleSections = getInlineStyleSections(
		block,
		['COLOR', 'BGCOLOR', 'FONTSIZE', 'FONTFAMILY'],
		section.start,
		section.end
	);
	inlineStyleSections.forEach(styleSection => {
		entityInlineMarkup.push(getInlineStyleSectionMarkup(block, styleSection));
	});
	let sectionText = entityInlineMarkup.join('');
	if (section.type === 'ENTITY') {
		if (section.entityKey !== undefined && section.entityKey !== null) {
			sectionText = getEntityMarkup(entityMap, section.entityKey, sectionText, customEntityTransform); // eslint-disable-line max-len
		}
	} else if (section.type === 'HASHTAG') {
		sectionText = `<a href="${sectionText}" class="wysiwyg-hashtag">${sectionText}</a>`;
	}
	return sectionText;
}

/**
 * Function will return the markup for block preserving the inline styles and
 * special characters like newlines or blank spaces.
 */
export function getBlockInnerMarkup(block, entityMap, hashtagConfig, customEntityTransform) {
	const blockMarkup = [];
	const sections = getSections(block, hashtagConfig);
	sections.forEach((section, index) => {
		let sectionText = getSectionMarkup(block, entityMap, section, customEntityTransform);
		if (index === 0) {
			sectionText = trimLeadingZeros(sectionText);
		}
		if (index === sections.length - 1) {
			sectionText = trimTrailingZeros(sectionText);
		}
		blockMarkup.push(sectionText);
	});
	return blockMarkup.join('');
}

function escapeDataString(dataSlateValue) {
	return JSON.stringify(dataSlateValue).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/**
 * Function will return html for the block.
 */
export function getBlockMarkup(block, entityMap, hashtagConfig, directional, customEntityTransform) {
	const blockHtml = [];
	if (isAtomicEntityBlock(block)) {
		blockHtml.push(getEntityMarkup(entityMap, block.entityRanges[0].key, undefined, customEntityTransform));
	} else {
		const blockTag = getBlockTag(block.type);
		if (blockTag) {
			blockHtml.push(`<${blockTag}`);

			const blockClass = getBlockClass(block.type);
			if (blockClass) {
				blockHtml.push(` class="${blockClass}"`);
			}
			if (directional) {
				blockHtml.push(' dir = "auto"');
			}
			blockHtml.push('>');
			if (isJiraNoneditable(block.type)) {
				const dataSlateValue = block.data;
				dataSlateValue.type = block.type;
				const escapedDataString = escapeDataString(dataSlateValue);
				blockHtml.push(
					`<span class="slate-non-editable-content" data-slate-value="${escapedDataString}" contenteditable="false">`
				);
			}
			let innerMarkup = getBlockInnerMarkup(block, entityMap, hashtagConfig, customEntityTransform);
			if (block.type === 'code') {
				// A code block's line breaks are emitted as <br> (see getSectionText's '\n' case),
				// but Plate's own default code-block HTML deserializer reads a flat <pre>'s
				// textContent and splits on a literal "\n" — a <br> node contributes nothing to
				// textContent, so two lines would collapse into one with no separator at all
				// (PIKA-542 / TC010). Swap back to a real newline so the emitted HTML round-trips
				// through Plate's unmodified default deserializer.
				innerMarkup = innerMarkup.replace(/<br>/g, '\n');
			}
			blockHtml.push(innerMarkup);

			if (isJiraNoneditable(block.type)) {
				blockHtml.push(`</span>`);
			}
			if (!isSelfClosingTag(blockTag)) {
				blockHtml.push(`</${blockTag}>`);
			}
		}
	}
	blockHtml.push('\n');
	return blockHtml.join('');
}
